<template>
  <!-- Note that we need 'overflow-visible' for the sticky-scroll to work -->
  <div class="start">

    <send-sample-dialog v-if="sample" :sample="sample" :hash="$route.hash" />

    <!-- TODO: explore something like this, perhaps just with one static fixed image / video -->
    <!--
    <div v-if="focused" style="position: fixed; top: 0; width: 100vw; height: 100vh; z-index: -1;">
      <div
        v-if="focused.skin.page_background_color"
        class="absolute"
        style="z-index: 0; width: 100%; height: 100%; transition: background-color .3s;"
        v-bind:style="{ 'background-color': focused.skin.page_background_color }"
      />
    </div>
    -->

    <start-header :bg="backgrounds[backgroundI].bg" @loaded-bg="enqueueNextBackground">
      <template v-slot:h1>
        <div class="text-center" v-bind:style="{ color: backgrounds[backgroundI].color }">
          Greeting cards and invitations for any occasion
        </div>
      </template>
      <template v-slot:append-inner>
        <div class="text-center">
          <v-btn
            class="btn-pop"
            large rounded dark color="ec-blue"
            :title="backgrounds[backgroundI].title + ' demo'"
            :to="{ hash: '#demo-' + backgrounds[backgroundI].name }"
          >
            Try demo…
          </v-btn>
        </div>
      </template>
    </start-header>

    <div class="mt-n16 pt-6 overflow-clip">
      <div
        class="white pt-6 mx-n16 px-16"
        style="border-radius: 50% / 32px; border-bottom-left-radius: 0; border-bottom-right-radius: 0; box-shadow: 0 0 24px #0008;"
      >

        <sticky-scroll class="mt-12 mb-0 overflow-visible relative" style="z-index: 4;">
          <v-container id="search" class="d-flex pr-md-25pct" fluid>
            <div id="search-field" class="flex-grow-1 relative">
              <v-autocomplete
                ref="search"
                class="white"
                v-bind:class="{ 'elevation-20': $vuetify.breakpoint.mdAndUp }"
                style="pointer-events: auto;"
                outlined dense rounded hide-details clearable single-line label="Search all categories" item-text="title"
                attach="#search-field"
                :items="categoriesFilter"
                :search-input.sync="searchInput"
                v-model="search"
                @keydown.enter="() => searchInput && $router.push({ name: 'catalog-category', params: { category: 'all' }, query: { q: searchInput } })"
              >
                <template v-slot:item="{ item }">
                  <v-list-item :to="{ name: 'catalog-category', params: { category: item.name } }">
                    <v-icon class="mr-2" v-bind:class="{ 'icon-animated': item.title == 'Animated' }" v-html="item.icon" />
                    <v-list-item-title v-text="item.title" />
                  </v-list-item>
                </template>
                <template v-slot:no-data>
                  <div>
                    <v-subheader v-if="loadStatus.pending" class="grey--text font-italic">Loading categories…</v-subheader>
                  </div>
                </template>
                <template v-slot:prepend-item>
                  <v-list-item
                    v-if="searchInput"
                    :to="{ name: 'catalog-category', params: { category: 'all' }, query: { q: searchInput } }"
                  >
                    <v-icon class="mr-2" v-html="mdiMagnify" />
                    <v-list-item-title>
                      Search all cards / invitations for <span class="font-italic">{{searchInput}}</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
            <v-btn
              v-blur
              class="ml-3"
              v-bind:class="{ 'elevation-20': $vuetify.breakpoint.mdAndUp }"
              fab small color="primary" aria-label="Search"
              :loading="loadStatus.pending"
              @click="() => {
                if (searchInput) {
                  $router.push({ name: 'catalog-category', params: { category: 'all' }, query: { q: searchInput } });
                } else {
                  $refs.search.focus();
                  $refs.search.isMenuActive = true;
                }
              }"
            >
              <v-icon v-html="mdiMagnify" />
            </v-btn>
          </v-container>

          <template v-if="$vuetify.breakpoint.smAndDown">
            <div
              ref="xs-preview-bg"
              class="absolute-fill mt-n9"
              style="z-index: -1; background: linear-gradient(#FFF 10%, #FAFAFA 90%, #FAFAFA00);"
            />
            <router-link
              v-if="focused"
              class="mt-3 d-block mx-auto relative"
              style="max-width: min(60%, 400px); aspect-ratio: 16 / 10;"
              :to="{ name: 'catalog-category', params: { category: focused.name } }"
            >
              <outline-laptop style="z-index: 0;">
                <skin-preview
                  class="absolute-fill"
                  from="Example sender"
                  to="Example recipient"
                  mode="preview"
                  :skin="focused.skin"
                  :bg-scale="$vuetify.breakpoint.xsOnly ? [ 160, 90 ] : [ 320, 180 ]"
                />
              </outline-laptop>
              <outline-phone class="absolute" style="z-index: 1; bottom: 5%; right: 0; width: 22%;" vertical>
                <skin-preview
                  class="absolute-fill"
                  from="Example sender"
                  to="Example recipient"
                  mode="preview"
                  :skin="focused.skin"
                  :bg-scale="$vuetify.breakpoint.xsOnly ? [ 160, 90 ] : [ 320, 180 ]"
                />
              </outline-phone>
              <picture v-for="preview, i in focused.preview" :key="focused.title + i" class="preview-card">
                <source v-if="preview.mp4" type="video/mp4" :srcset="preview.mp4">
                <img
                  class="d-block absolute elevation-20"
                  loading="lazy"
                  :type="'image/' + (preview.webp ? 'webp' : 'jpeg')"
                  :src="preview.webp ? preview.webp : preview.jpg"
                  :alt="`${focused.title} sample card page ${i + 1}`"
                />
              </picture>
            </router-link>
          </template>
        </sticky-scroll>

        <div class="my-0 px-0">

          <!-- Make sure the bottom edge of the shadow is not visible while the categories are loading -->
          <!-- <div class="absolute-fill grey lighten-5" style="z-index: -2;" /> -->

          <!-- In cases where we're only clipping a solid background color and the height of the clipped container may change (i.e. when we load the categories), this approach is much more reliable. Note that I sometimes see the beginnings of the drop-shadow as an artifact on the underside, hence the clip-path using 1px instead of 0 (on Chrome). -->
          <!-- <div style="filter: drop-shadow(0 0 24px #0008); clip-path: inset(-100vw -100vw 1px -100vw); margin-top: -3vw;">
            <clip-waves class="grey lighten-5" :offset=".75">
              <div style="height: calc(3vw + 12px);" />
            </clip-waves>
          </div> -->


          <!--
          <v-container v-if="!loadCategoriesStatus.pending" class="py-md-0" fluid>
            <v-checkbox
              hide-details label="Show greeting card categories only"
              :input-value="spotlight == 1"
              @click="$router.push({ query: { ...$route.query, type: spotlight == 1 ? undefined : 'greeting-cards' } })"
            />
            <v-checkbox
              label="Show invitation categories only"
              :input-value="spotlight == 2"
              @click="$router.push({ query: { ...$route.query, type: spotlight == 2 ? undefined : 'invitations' } })"
            />
          </v-container>
          -->

          <div class="py-6" />

          <template v-for="c, j in categories">
            <v-container
              v-if="j && c.seasonality && c.seasonality != categories[j-1].seasonality"
              class="pt-6 pb-0 text--label-active"
              fluid
            >
              <template v-if="c.seasonality == 1">Featured seasonal occasions</template>
              <template v-else-if="c.seasonality == 3">Other occasions</template>
              <template v-else>&nbsp;</template>
            </v-container>

            <v-list-item
              v-if="!spotlight || !c.type || spotlight == c.type"
              ref="category"
              class="pa-0 item"
              v-bind:class="{ active: focused === c }"
              :to="{ name: 'catalog-category', params: { category: c.name } }"
              @mouseover.native="onMouseover(c)"
              @touchstart.native="focused = c"
            >
              <v-container class="d-flex align-center overflow-visible relative pa-6 pl-md-3 pr-md-25pct" fluid>
                <div class="mr-6 flex-grow-1">
                  <div class="d-flex align-center">
                    <v-icon class="c-icon" v-bind:style="{ color: c.icon_color || '#28547e' }" v-html="c.icon" />
                    <h3 class="c-title title header-font ml-2" v-bind:style="{ color: c.title_color || '#28547e' }" v-html="c.title" />
                  </div>
                  <div v-if="c.description" class="c-desc mt-2 ml-8 text-body-2 description" v-bind:style="{ color: c.title_color || '#28547e' }" v-html="c.description" />
                </div>

                <v-icon class="right" v-html="mdiChevronRight" />

                <v-lazy v-if="$vuetify.breakpoint.mdAndUp" class="flex-grow-0 absolute overflow-visible item-preview">
                  <div>
                    <outline-laptop style="z-index: 0;">
                      <skin-preview
                        class="absolute-fill"
                        from="Example sender"
                        to="Example recipient"
                        mode="preview"
                        :skin="c.skin"
                      />
                    </outline-laptop>
                    <outline-phone class="absolute" style="z-index: 1; bottom: 5%; right: 0; width: 22%;" vertical>
                      <skin-preview
                        class="absolute-fill"
                        from="Example sender"
                        to="Example recipient"
                        mode="preview"
                        :skin="c.skin"
                      />
                    </outline-phone>
                    <picture v-for="preview, i in c.preview" :key="c.title + i" class="preview-card">
                      <source v-if="preview.mp4" type="video/mp4" :srcset="preview.mp4">
                      <img
                        class="d-block absolute elevation-20"
                        loading="lazy"
                        :type="'image/' + (preview.webp ? 'webp' : 'jpeg')"
                        :src="preview.webp ? preview.webp : preview.jpg"
                        :alt="`${c.title} sample card page ${i + 1}`"
                      />
                    </picture>
                  </div>
                </v-lazy>

              </v-container>
            </v-list-item>

          </template>

          <div class="bottom-spacer" />

        </div>
      </div>
    </div>

  </div>
</template>


<style scoped>
.shadow {
  color: #111;
  text-shadow: 0 0 1rem #FFF, 0 0 2rem #FFF;
  box-shadow: 0 0 128px 128px #FFFB;
  background-color: #FFFB;
}

.preview-card img {
  width: 31.5%;
  aspect-ratio: 5 / 7;
  bottom: 0;
  background-color: #FFF;
  transition: left .3s;
}
.preview-card:nth-child(3) img { z-index: 3; left: 2.8%; transform: rotate(-5deg); }
.preview-card:nth-child(4) img { z-index: 2; left: 22.4%; }
.preview-card:nth-child(5) img { z-index: 1; left: 42%; }
.preview-card:last-child img { transform: rotate(5deg); }
.preview-card:last-child:nth-child(3) img { transform: none; }



.item .item-preview {
  aspect-ratio: 16 / 10;
  pointer-events: none !important;
  right: -5%;
  width: 40%;
  opacity: 0;
  transition: opacity .3s;
}

.item.active .item-preview {
  opacity: 1;
}

.item:not(.active) .item-preview .preview-card img {
  left: 0 !important;
}

.container--fluid {
  max-width: max(960px, min(80%, 1785px));
}

.item {
  padding: 0 16px;
}

.pr-md-25pct {
  padding-right: 12px;
}

.bottom-spacer {
  height: 12px;
}

@media (min-width: 960px) {
  .pr-md-25pct {
    padding-right: 25% !important;
  }

  .bottom-spacer {
    height: 144px;
  }
}

.item .c-icon,
.item .c-title,
.item .c-desc {
  transition: transform .3s;
}

.item .c-icon {
  transform-origin: right;
}

.item .c-title,
.item .c-desc {
  margin-right: 9%;
  transform-origin: left;
  line-height: 1.2em;
}

.item.active {
  background-color: rgba(0, 0, 0, 0.04);
}

.item.active .c-icon {
  transform: scale(1.4);
}

.item.active .c-title,
.item.active .c-desc {
  transform: scale(1.1);
}

.item.active {
  outline: 2px solid #1976d2;
}

@media (min-width: 600px) {
  .item.active {
    outline-color: transparent;
  }
}

.item.active .right {
  color: #1976d2 !important;
}
</style>


<script>
import OutlineLaptop from '@/components/OutlineLaptop.vue';
import OutlinePhone from '@/components/OutlinePhone.vue';
import SendSampleDialog from '@/components/SendSampleDialog.vue';
import SkinPreview from '@/components/SkinPreview.vue';
import StartHeader from '@/components/StartHeader.vue';
import StartLayer from '@/components/StartLayer.vue';
import StickyScroll from '@/components/StickyScroll.vue';
import loadCategories from '@/mixins/load-categories.js';
import { publicDesign, blankDesign, blankDesignId } from '@/mixins/public-design.js';
import { DEFAULT as DEFAULT_SKIN } from '@/utils/skin.js';
import iconPathCardPersonal from 'raw-loader!@/assets/icon-path-card-personal.txt';
import iconPathCardInvitation from 'raw-loader!@/assets/icon-path-card-invitation.txt';
import iconPathCardsInvitation from 'raw-loader!@/assets/icon-path-cards-invitation.txt';
import { mdiDomain, mdiEmailEdit, mdiFilterVariant, mdiFrequentlyAskedQuestions, mdiMagnify, mdiCards, mdiChevronUp, mdiChevronDown, mdiChevronRight, mdiMenuDown } from '@mdi/js';


const PARALLAX_PCT = .5;


// Pin these categories to the top of the list
const SORT_TOP = [ 'greeting-cards', 'invitations' ];

export default {
  name: 'catalog-categories',

  routerViewMeta: {
    $class: 'fade-transparent',
    $subtitle: 'Categories',
    description: 'Personal cards, group cards, and invitations for any occasion',
    'image': 'https://ellacard.com/start/header-cards-large.jpg',
    'image:type': 'image/jpeg',
    'image:width': '3307',
    'image:height': '990'
  },

  components: {
    OutlineLaptop,
    OutlinePhone,
    SendSampleDialog,
    SkinPreview,
    StartHeader,
    StartLayer,
    StickyScroll
  },

  mixins: [ loadCategories ],

  data() {
    return {
      sample: null,
      search: '',
      searchInput: '',
      categories: [],
      categoriesFilter: [],
      backgroundI: 0,
      focused: null,
      previouslyFocused: new Set(),
      loadStatus: this.$track('load'),
      loadDemoStatus: this.$track('loadDemo')
    };
  },

  computed: {
    hydrated() {
      return !this.loadStatus.pending && !this.loadDemoStatus.pending;
    },

    spotlight() {
      if (this.$route.query.type == 'greeting-cards')
        return 1;
      if (this.$route.query.type == 'invitations')
        return 2;
      return 0;
    },

    backgrounds() {
      const pool = [
        {
          name: 'all',
          title: 'All greeting cards and invitations',
          color: '#FFF',
          bg: { src: 'https://cdn.ellacard.com/page_backgrounds/Light Bokeh.mp4', color: '#d1ad7a' }
        }
      ];
      const imgs = new Set([ 'https://cdn.ellacard.com/page_backgrounds/Light Bokeh.160x90.mp4' ]);
      const sorted = this.categories.filter(
        x => (x.seasonality >= 0) && (imgs.size < imgs.add(x.skin.page_background_img).size)
      );
      sorted.sort((a, b) => a.seasonality - b.seasonality);
      pool.push(
        ...sorted.map(
          x => ({
            name: x.name,
            title: x.title,
            color: x.title_color,
            bg: {
              src: x.skin.page_background_img ? x.skin.page_background_img.replace(/\.160x90/, '') : null,
              color: x.skin.page_background_color
            }
          })
        )
      );
      return pool;
    }
  },

  created() {
    Object.assign(this, { PARALLAX_PCT, mdiDomain, mdiEmailEdit, mdiFilterVariant, mdiFrequentlyAskedQuestions, mdiMagnify, mdiChevronDown, mdiChevronUp, mdiChevronRight, mdiMenuDown, mdiCards, iconPathCardPersonal, iconPathCardInvitation, iconPathCardsInvitation });
  },

  mounted() {
    addEventListener('scroll', this.onScroll);
    this.onScroll();
  },

  destroyed() {
    removeEventListener('scroll', this.onScroll);
    clearTimeout(this.updateFocusedTimeout);
    clearTimeout(this.backgroundT);
  },

  methods: {
    onScroll(e) {
      // Adjust parallax effect on header image
      // requestAnimationFrame(() => {
      //   const background = this.$refs.background.$el;
      //   const parent = background.parentNode;
      //   const pct = Math.min(1, Math.max(0, (parent.offsetTop + parent.offsetHeight - scrollY) / (innerHeight + parent.offsetHeight)));
      //   background.style.top = (-PARALLAX_PCT * parent.offsetHeight * pct) + 'px';
      // });

      if (this.focused)
        this.enqueueUpdateFocused(e.timeStamp);
    },

    enqueueUpdateFocused(t, delay=0) {
      clearTimeout(this.updateFocusedTimeout);
      this.updateFocusedTimeout = setTimeout(() => requestAnimationFrame(() => this.updateFocused(t)), delay);
    },

    getOffset(i) {
      const el = this.$refs.category[i]?.$el;
      if (!el || !this.$refs['xs-preview-bg'])
        return 0;
      const { top } = el.getBoundingClientRect();
      if (top < this.$refs['xs-preview-bg'].offsetHeight)
        // Scrolled position is too high
        return 1;
      if (top > innerHeight)
        // Scrolled position is too low
        return -1;
      return 0;
    },

    updateFocused(t) {
      // Control the focused preview for touch-only screens
      if (this.hasMouseover)
        return;

      if (t - (this.lastUpdateFocused || 0) < 200) {
        // We've alread updated the focused category within the last 200ms
        this.enqueueUpdateFocused(t + 200, 200);
        return;
      }

      this.lastUpdateFocused = t;

      // Change the currently focused element to the nearest visible one
      let i = this.categories.findIndex(x => x === this.focused);
      for (let offset; offset = this.getOffset(i); offset && (i >= 0) && (i < this.categories.length))
        i += offset;
      if (i < 0 || i >= this.categories.length)
        // Nothing is visible
        return;

      this.focused = this.categories[i];
    },

    onMouseover(category) {
      this.hasMouseover = true;
      this.focused = category;
    },

    async load() {
      this.categories = await this.loadCategories(true);
      this.categoriesFilter = this.categories.filter(x => x.seasonality > 0); // Filter out org and "all" categories
      this.categoriesFilter.sort((a, b) => a.title.localeCompare(b.title));
    },

    async loadDemo(hash) {
      const name = hash.startsWith('#demo-') && hash.substr(6).split(':')[0];
      if (name == this.sample?.categoryName)
        return;
      this.sample = null;
      if (name) {
        const header = await this.$call('get-catalog-header', { name });
        this.sample = header && { categoryName: name, ...header.order };
      }
    },

    enqueueNextBackground() {
      clearTimeout(this.backgroundT);
      if (this.backgrounds.length > 1)
        this.backgroundT = setTimeout(() => this.backgroundI = (this.backgroundI + 1) % this.backgrounds.length, 3000);
    }
  },

  watch: {
    '$route.hash': {
      immediate: true,
      handler(v) {
        this.loadDemo(v);
      }
    },

    backgrounds: {
      immediate: true,
      handler() {
        clearTimeout(this.backgroundT);
        this.backgroundI = 0;
      }
    },

    focused(v) {
      if (v)
        this.previouslyFocused.add(v.name);
    },

    loadCategoriesKey: {
      immediate: true,
      handler() {
        this.load();
      }
    },

    categories(v) {
      this.focused = v.find(x => !this.spotlight || !x.type || this.spotlight == x.type);
    },

    spotlight(v) {
      if (!v || !this.focused?.type || v == this.focused.type)
        // The focused element is not hidden due to the spotlight change
        return;
      this.focused = this.categories.find(x => !v || !x.type || v == x.type);
    }
  }
}
</script>
